import { useEffect, useState, useMemo, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Building2, Boxes, ShieldCheck, ClipboardList, AlertTriangle, CalendarDays,
  ArrowUpRight, ArrowDownRight, TrendingUp, Search, Bell, HelpCircle, Star, Filter,
  Download, FileCheck2, Cloud, Flag, Users as UsersIcon, MapPin,
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { useAuth } from "@/lib/auth";

// -----------------------------------------------------------------------------
// KPI tile
// -----------------------------------------------------------------------------
const KPI_TILES = [
  { key: "open_lots", label: "Open Lots", icon: Boxes, ring: "bg-orange-50 text-orange-700 ring-orange-100" },
  { key: "pending_hold_points", label: "Pending Hold Points", icon: ShieldCheck, ring: "bg-violet-50 text-violet-700 ring-violet-100" },
  { key: "closed_lots", label: "Closed Lots", icon: FileCheck2, ring: "bg-emerald-50 text-emerald-700 ring-emerald-100" },
  { key: "itp_completion_pct", label: "ITP Completion", icon: ClipboardList, ring: "bg-sky-50 text-sky-700 ring-sky-100", suffix: "%" },
  { key: "open_ncrs", label: "Open NCRs", icon: AlertTriangle, ring: "bg-rose-50 text-rose-700 ring-rose-100" },
];

function KpiTile({ tile, data, href }) {
  const value = data?.value ?? 0;
  const delta = data?.delta_7d;
  const Icon = tile.icon;
  const trendUp = (delta ?? 0) >= 0;
  const Wrap = href ? Link : "div";
  const wrapProps = href ? { to: href } : {};
  return (
    <Wrap {...wrapProps} className={href ? "block h-full transition-transform hover:-translate-y-0.5" : "block h-full"} data-testid={`kpi-${tile.key}-link`}>
      <Card className={`flex h-full flex-col border-slate-200 p-4 ${href ? "cursor-pointer hover:border-teal-300 hover:shadow-sm" : ""}`} data-testid={`kpi-${tile.key}`}>
        <div className="flex items-center justify-between">
          <div className={`grid size-10 place-items-center rounded-lg ring-4 ${tile.ring}`}>
            <Icon className="size-5" />
          </div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Live</div>
        </div>
        <div className="mt-2 text-xs font-medium text-slate-500">{tile.label}</div>
        <div className="mt-0.5 font-heading text-3xl font-black tracking-tighter text-slate-900" data-testid={`kpi-${tile.key}-value`}>
          {value}{tile.suffix || ""}
        </div>
        <div className={`mt-auto flex min-h-[18px] items-center gap-1 pt-1 text-xs font-semibold ${trendUp ? "text-emerald-600" : "text-rose-600"}`} data-testid={`kpi-${tile.key}-delta`}>
          {delta !== null && delta !== undefined && (
            <>
              {trendUp ? <ArrowUpRight className="size-3" /> : <ArrowDownRight className="size-3" />}
              <span>{Math.abs(delta)} vs last 7 days</span>
            </>
          )}
        </div>
      </Card>
    </Wrap>
  );
}

// -----------------------------------------------------------------------------
// Main Dashboard
// -----------------------------------------------------------------------------
export default function Dashboard() {
  const routeProjectId = useParams().projectId;
  const inProjectMode = !!routeProjectId;
  const [data, setData] = useState(null);
  const [projectId, setProjectId] = useState(routeProjectId || "all");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  // In project mode, always pin to the route project.
  useEffect(() => {
    if (routeProjectId && projectId !== routeProjectId) setProjectId(routeProjectId);
  }, [routeProjectId, projectId]);

  useEffect(() => {
    setLoading(true);
    const q = projectId && projectId !== "all" ? `?project_id=${projectId}` : "";
    api.get(`/dashboard/v2${q}`).then((r) => setData(r.data)).catch((e) => setErr(e.message)).finally(() => setLoading(false));
  }, [projectId]);

  const base = routeProjectId ? `/p/${routeProjectId}` : "";
  const kpiHrefs = {
    open_lots: `${base}/lots`,
    pending_hold_points: `${base}/lots`,
    closed_lots: `${base}/lots`,
    itp_completion_pct: `${base}/itp`,
    open_ncrs: `${base}/ncrs`,
  };
  if (err) return <div className="p-8 text-rose-700" data-testid="dashboard-error">Failed to load dashboard: {err}</div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      <TopBar projectId={projectId} setProjectId={setProjectId} projects={data?.projects || []} lockedProjectId={inProjectMode ? routeProjectId : null} />
      <div className="mx-auto max-w-[1800px] space-y-4 p-4 md:p-6">
        {/* Action row */}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700" data-testid="dashboard-date-range">
            <CalendarDays className="mr-1 inline size-4 text-slate-500" />
            Last 7 days
          </div>
          <Button variant="outline" size="sm" data-testid="dashboard-filters-btn"><Filter className="mr-1 size-4" /> Filters</Button>
          <Button size="sm" className="bg-orange-600 hover:bg-orange-700" data-testid="dashboard-export-btn"><Download className="mr-1 size-4" /> Export Report</Button>
        </div>

        {/* KPI row */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
          {KPI_TILES.map((t) => <KpiTile key={t.key} tile={t} data={data?.kpis?.[t.key]} href={kpiHrefs[t.key]} />)}
        </div>

        {/* Row 1 — Project Overview + Lot Status + HP/WP */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <ProjectOverviewCard project={data?.project_overview} loading={loading} base={base} />
          <LotStatusCard lotStatus={data?.lot_status} loading={loading} base={base} />
          <HoldPointsCard hp={data?.hold_points} loading={loading} base={base} />
        </div>

        {/* Row 2 — NCR + Recent Activity + ITP Progress */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <NCRCard ncrs={data?.ncrs} loading={loading} base={base} />
          <ActivityCard activity={data?.recent_activity} loading={loading} base={base} />
          <ITPProgressCard progress={data?.itp_progress} team={data?.team_at_a_glance} loading={loading} base={base} />
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Global Search (dashboard omnibox)
// -----------------------------------------------------------------------------
function GlobalSearch({ projectId }) {
  const [q, setQ] = useState("");
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Flatten items for keyboard navigation
  const flatItems = useMemo(
    () => (groups || []).flatMap((g) => g.items.map((it) => ({ ...it, groupType: g.type }))),
    [groups]
  );

  // Debounced fetch
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setGroups([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(() => {
      const params = new URLSearchParams({ q: term });
      if (projectId && projectId !== "all") params.set("project_id", projectId);
      api.get(`/search?${params.toString()}`)
        .then((r) => { setGroups(r.data?.groups || []); setActiveIdx(0); })
        .catch(() => setGroups([]))
        .finally(() => setLoading(false));
    }, 220);
    return () => clearTimeout(t);
  }, [q, projectId]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Global ⌘K / Ctrl+K shortcut
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const go = (item) => {
    setOpen(false);
    setQ("");
    setGroups([]);
    if (item?.href) navigate(item.href);
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, flatItems.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (flatItems[activeIdx]) go(flatItems[activeIdx]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  const term = q.trim();
  const showPanel = open && term.length >= 2;

  return (
    <div ref={wrapRef} className="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
      <input
        ref={inputRef}
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Search lots, ITPs, documents, NCRs…"
        className="h-10 w-full rounded-md border border-slate-200 bg-white pl-9 pr-14 text-sm focus:border-teal-500 focus:outline-none"
        data-testid="dashboard-search-input"
      />
      <kbd className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded border border-slate-200 bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">⌘K</kbd>

      {showPanel && (
        <div
          className="absolute left-0 right-0 top-11 z-30 max-h-[70vh] overflow-auto rounded-md border border-slate-200 bg-white shadow-xl"
          data-testid="dashboard-search-results"
        >
          {loading && (
            <div className="p-3 text-xs text-slate-500" data-testid="dashboard-search-loading">Searching…</div>
          )}
          {!loading && flatItems.length === 0 && (
            <div className="p-3 text-xs text-slate-500" data-testid="dashboard-search-empty">
              No results for “{term}”.
            </div>
          )}
          {!loading && groups.map((g, gi) => {
            let cursor = groups.slice(0, gi).reduce((acc, gg) => acc + gg.items.length, 0);
            return (
              <div key={g.type} data-testid={`dashboard-search-group-${g.type}`}>
                <div className="sticky top-0 border-b border-slate-100 bg-slate-50 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  {g.label}
                </div>
                <ul>
                  {g.items.map((it) => {
                    const idx = cursor++;
                    const active = idx === activeIdx;
                    return (
                      <li key={`${g.type}-${it.id}`}>
                        <button
                          type="button"
                          onMouseEnter={() => setActiveIdx(idx)}
                          onClick={() => go(it)}
                          className={`flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 ${active ? "bg-teal-50" : ""}`}
                          data-testid={`dashboard-search-result-${g.type}-${it.id}`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-semibold text-slate-900">{it.title}</div>
                            {it.subtitle && <div className="truncate text-xs text-slate-500">{it.subtitle}</div>}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Top bar
// -----------------------------------------------------------------------------
function TopBar({ projectId, setProjectId, projects, lockedProjectId }) {
  const { user } = useAuth();
  return (
    <div className="sticky top-0 z-20 flex items-center gap-3 border-b border-slate-200 bg-white/95 px-4 py-2 backdrop-blur md:px-6" data-testid="dashboard-topbar">
      {!lockedProjectId && (
        <div className="flex items-center gap-2">
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="h-10 w-[300px]" data-testid="dashboard-project-select">
              <SelectValue placeholder="Select project" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="dashboard-project-all">All Projects</SelectItem>
              {(projects || []).map((p) => (
                <SelectItem key={p.id} value={p.id} data-testid={`dashboard-project-option-${p.id}`}>
                  {p.number ? `${p.number} — ` : ""}{p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-800" data-testid="dashboard-live-badge">
        <span className="size-1.5 rounded-full bg-emerald-500" /> Live
      </span>
      <button className="text-slate-400 hover:text-amber-500" aria-label="favourite" data-testid="dashboard-fav"><Star className="size-4" /></button>
      <div className="mx-2 flex-1">
        <GlobalSearch projectId={projectId} />
      </div>
      <div className="flex items-center gap-2">
        <button className="relative rounded-md p-2 text-slate-500 hover:bg-slate-100" aria-label="notifications" data-testid="dashboard-notifications-btn">
          <Bell className="size-4" />
          <span className="absolute -top-0.5 -right-0.5 grid size-4 place-items-center rounded-full bg-rose-500 text-[9px] font-bold text-white">12</span>
        </button>
        <button className="rounded-md p-2 text-slate-500 hover:bg-slate-100" aria-label="help" data-testid="dashboard-help-btn"><HelpCircle className="size-4" /></button>
        <div className="ml-2 hidden items-center gap-2 border-l border-slate-200 pl-3 md:flex" data-testid="dashboard-user">
          <div className="grid size-8 place-items-center rounded-full bg-teal-100 text-xs font-bold text-teal-800">{(user?.name || "U").slice(0, 2).toUpperCase()}</div>
          <div className="min-w-0">
            <div className="text-xs font-semibold text-slate-900">{user?.name || "User"}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{user?.role?.replace("_", " ") || ""}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Project Overview Card
// -----------------------------------------------------------------------------
function ProjectOverviewCard({ project, loading, base = "" }) {
  if (loading) return <Card className="p-4 lg:col-span-1"><SkeletonBlock height={280} /></Card>;
  if (!project) return (
    <Card className="border-slate-200 p-6 text-center" data-testid="project-overview-empty">
      <div className="mx-auto grid size-12 place-items-center rounded-full bg-slate-100 text-slate-500"><Building2 className="size-5" /></div>
      <div className="mt-2 text-sm font-semibold text-slate-900">No project selected</div>
      <div className="mt-1 text-xs text-slate-500">Create a project or pick one from the top selector to see the overview here.</div>
      <Link to="/projects" className="mt-3 inline-block text-xs font-semibold text-teal-700 hover:underline">Go to Projects →</Link>
    </Card>
  );
  return (
    <Card className="overflow-hidden border-slate-200" data-testid="project-overview-card">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2">
        <h3 className="font-heading text-sm font-black tracking-tighter text-slate-900">Project Overview</h3>
        <Link to={`/projects/${project.id}`} className="text-xs font-semibold text-teal-700 hover:underline" data-testid="project-overview-view-link">View Project →</Link>
      </div>
      <div className="grid gap-3 p-4 md:grid-cols-[160px_1fr]">
        <div className="h-24 overflow-hidden rounded-lg bg-slate-200 md:h-32">
          <img src={project.thumbnail_url || "/brand/project-fallback.jpg"} alt="" className="h-full w-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
        </div>
        <div className="min-w-0">
          <div className="text-lg font-bold leading-tight text-slate-900" data-testid="project-overview-name">{project.name}</div>
          <div className="mt-1 flex items-center gap-1 text-xs text-slate-500"><MapPin className="size-3" /> {project.location}</div>
          <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            <dt className="text-slate-500">Client</dt><dd className="text-right font-medium text-slate-800">{project.client}</dd>
            <dt className="text-slate-500">Principal Contractor</dt><dd className="text-right font-medium text-slate-800">{project.principal_contractor}</dd>
            <dt className="text-slate-500">Contract No.</dt><dd className="text-right font-mono text-[11px] text-slate-800">{project.contract_number}</dd>
          </dl>
        </div>
      </div>
      <div className="grid grid-cols-2 border-t border-slate-100 text-xs">
        <MiniStat label="Time Elapsed" value={`${project.elapsed_pct || 0}%`} progress={project.elapsed_pct || 0} />
        <MiniStat label="Planned Completion" value={project.planned_completion ? new Date(project.planned_completion).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "TBC"} />
      </div>
    </Card>
  );
}

function MiniStat({ label, value, progress, accent }) {
  return (
    <div className="border-r border-slate-100 p-3 last:border-r-0">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-0.5 text-sm font-bold ${accent === "emerald" ? "text-emerald-700" : "text-slate-900"}`}>{value}</div>
      {progress !== undefined && (
        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
          <div className="h-full bg-orange-500 transition-all" style={{ width: `${progress}%` }} />
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Lot Status Card (donut)
// -----------------------------------------------------------------------------
function LotStatusCard({ lotStatus, loading, base = "" }) {
  if (loading || !lotStatus) return <Card className="p-4"><SkeletonBlock height={280} /></Card>;
  const buckets = lotStatus.buckets || [];
  const total = lotStatus.total || 0;
  const chartData = buckets.filter((b) => b.count > 0);
  return (
    <Card className="overflow-hidden border-slate-200" data-testid="lot-status-card">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2">
        <h3 className="font-heading text-sm font-black tracking-tighter text-slate-900">Lot Status</h3>
      </div>
      <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2">
        <div className="relative h-40" role="img" aria-label={`Lot status donut chart, total ${total} lots`}>
          <ResponsiveContainer>
            <PieChart>
              <Pie data={chartData.length ? chartData : [{ count: 1 }]} dataKey="count" innerRadius={45} outerRadius={70} paddingAngle={chartData.length > 1 ? 2 : 0}>
                {chartData.length ? chartData.map((b) => <Cell key={b.key} fill={b.colour} />) : <Cell fill="#e2e8f0" />}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 grid place-items-center">
            <div className="text-center">
              <div className="font-heading text-2xl font-black leading-none tracking-tighter text-slate-900" data-testid="lot-status-total">{total}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Total</div>
            </div>
          </div>
        </div>
        <ul className="space-y-1 text-xs">
          {buckets.map((b) => (
            <li key={b.key} className="flex items-center gap-2" data-testid={`lot-status-bucket-${b.key}`}>
              <span className="inline-block size-2.5 rounded-full" style={{ background: b.colour }} />
              <span className="flex-1 text-slate-600">{b.label}</span>
              <span className="text-slate-900 font-semibold">{b.count}</span>
              <span className="text-slate-400">({b.pct}%)</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="border-t border-slate-100 px-4 py-2 text-right">
        <Link to={`${base}/lots`} className="text-xs font-semibold text-teal-700 hover:underline" data-testid="lot-status-view-lots">View Lots →</Link>
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Hold Points & Witness Points Card
// -----------------------------------------------------------------------------
function HoldPointsCard({ hp, loading, base = "" }) {
  if (loading || !hp) return <Card className="p-4"><SkeletonBlock height={280} /></Card>;
  return (
    <Card className="overflow-hidden border-slate-200" data-testid="hold-points-card">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2">
        <h3 className="font-heading text-sm font-black tracking-tighter text-slate-900">Hold Point & Witness Point</h3>
        <Link to={`${base}/lots`} className="text-xs font-semibold text-teal-700 hover:underline" data-testid="hp-view-all">View All →</Link>
      </div>
      <div className="p-4 pt-3">
        <div className="mb-1 flex items-center justify-between">
          <span className="text-xs font-semibold text-slate-700">Pending Approvals</span>
          <span className="inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800" data-testid="hp-pending-count">{hp.pending_count}</span>
        </div>
        {hp.pending.length === 0 && <div className="py-4 text-center text-xs text-slate-400">No pending approvals</div>}
        <ul className="space-y-1">
          {hp.pending.map((p, i) => (
            <li key={`${p.code}-${i}`} className="flex items-center gap-2 text-xs" data-testid={`hp-pending-row-${i}`}>
              <span className="font-mono text-[10px] font-bold text-slate-500">{p.code || "—"}</span>
              <span className="flex-1 truncate text-slate-800">{p.title || "Untitled"}</span>
              <span className={`rounded border px-1.5 py-0.5 text-[9px] font-semibold ${p.kind === "Hold Point" ? "border-amber-300 bg-amber-50 text-amber-800" : "border-sky-300 bg-sky-50 text-sky-800"}`}>{p.kind}</span>
            </li>
          ))}
        </ul>
        {hp.more_pending > 0 && <div className="mt-2 text-xs text-slate-500">+ {hp.more_pending} more pending</div>}
        <div className="mt-4 mb-1 text-xs font-semibold text-slate-700">Recently Released</div>
        {hp.recent_released.length === 0 && <div className="py-2 text-center text-xs text-slate-400">Nothing released recently</div>}
        <ul className="space-y-1">
          {hp.recent_released.map((r, i) => (
            <li key={`${r.code}-${i}`} className="flex items-center gap-2 text-xs" data-testid={`hp-released-row-${i}`}>
              <FileCheck2 className="size-3 text-emerald-600" />
              <span className="font-mono text-[10px] font-bold text-slate-500">{r.code || "—"}</span>
              <span className="flex-1 truncate text-slate-800">{r.title || "Untitled"}</span>
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// NCR / Quality Issues Card
// -----------------------------------------------------------------------------
function NCRCard({ ncrs, loading, base = "" }) {
  if (loading || !ncrs) return <Card className="p-4"><SkeletonBlock height={220} /></Card>;
  const sevClasses = { High: "bg-rose-100 text-rose-800", Medium: "bg-amber-100 text-amber-800", Low: "bg-sky-100 text-sky-800" };
  const stClasses = { Open: "bg-rose-50 text-rose-800", "In Progress": "bg-amber-50 text-amber-800", Closed: "bg-emerald-50 text-emerald-800" };
  return (
    <Card className="overflow-hidden border-slate-200" data-testid="ncr-card">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2">
        <h3 className="font-heading text-sm font-black tracking-tighter text-slate-900">NCR / Quality Issues</h3>
        <Link to={`${base}/ncrs`} className="text-xs font-semibold text-teal-700 hover:underline" data-testid="ncr-view-all">View All →</Link>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
            <tr><th className="px-3 py-2">ID</th><th className="px-3 py-2">Title</th><th className="px-3 py-2">Severity</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Due Date</th></tr>
          </thead>
          <tbody>
            {ncrs.length === 0 && <tr><td colSpan={5} className="p-4 text-center text-slate-400" data-testid="ncr-empty">No NCRs — great!</td></tr>}
            {ncrs.map((n) => (
              <tr key={n.id} className="border-t border-slate-100" data-testid={`ncr-row-${n.id}`}>
                <td className="px-3 py-2 font-mono text-[11px] font-bold text-slate-700">{n.id}</td>
                <td className="px-3 py-2 text-slate-800 truncate max-w-[220px]">{n.title}</td>
                <td className="px-3 py-2"><span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${sevClasses[n.severity] || "bg-slate-100 text-slate-700"}`}>{n.severity}</span></td>
                <td className="px-3 py-2"><span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${stClasses[n.status] || "bg-slate-100 text-slate-700"}`}>{n.status}</span></td>
                <td className="px-3 py-2 text-slate-600">{n.due_date ? new Date(n.due_date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="border-t border-slate-100 px-4 py-2 text-center">
        <Link to={`${base}/ncrs`} className="text-xs font-semibold text-teal-700 hover:underline" data-testid="ncr-create-link">Create NCR +</Link>
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Recent Activity
// -----------------------------------------------------------------------------
const ACT_COLOURS = {
  green: "bg-emerald-100 text-emerald-700",
  amber: "bg-amber-100 text-amber-700",
  teal: "bg-teal-100 text-teal-700",
  rose: "bg-rose-100 text-rose-700",
  slate: "bg-slate-100 text-slate-700",
  blue: "bg-sky-100 text-sky-700",
};

function ActivityCard({ activity, loading, base = "" }) {
  if (loading || !activity) return <Card className="p-4"><SkeletonBlock height={260} /></Card>;
  return (
    <Card className="overflow-hidden border-slate-200" data-testid="activity-card">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2">
        <h3 className="font-heading text-sm font-black tracking-tighter text-slate-900">Recent Activity</h3>
        <Link to={`${base}/audit`} className="text-xs font-semibold text-teal-700 hover:underline" data-testid="activity-view-all">View All →</Link>
      </div>
      <ul className="divide-y divide-slate-100">
        {activity.length === 0 && <li className="p-6 text-center text-xs text-slate-400" data-testid="activity-empty">No recent activity</li>}
        {activity.map((a, i) => (
          <li key={i} className="flex items-start gap-2 px-4 py-2 text-xs" data-testid={`activity-row-${i}`}>
            <div className={`grid size-6 place-items-center rounded ${ACT_COLOURS[a.colour] || "bg-slate-100 text-slate-700"}`}>
              <TrendingUp className="size-3" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-slate-800">{a.label}</div>
              <div className="truncate text-slate-500">{a.subtitle || a.actor}</div>
            </div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">{a.when}</div>
          </li>
        ))}
      </ul>
      <div className="border-t border-slate-100 px-4 py-2 text-center">
        <Link to={`${base}/audit`} className="text-xs font-semibold text-teal-700 hover:underline" data-testid="activity-view-full">View Full Activity →</Link>
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// ITP Progress + Team at a Glance
// -----------------------------------------------------------------------------
function ITPProgressCard({ progress, team, loading, base = "" }) {
  if (loading || !progress) return <Card className="p-4"><SkeletonBlock height={260} /></Card>;
  return (
    <Card className="overflow-hidden border-slate-200" data-testid="itp-progress-card">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2">
        <h3 className="font-heading text-sm font-black tracking-tighter text-slate-900">ITP Progress</h3>
        <Link to={`${base}/itp`} className="text-xs font-semibold text-teal-700 hover:underline" data-testid="itp-view-itps">View ITPs →</Link>
      </div>
      <div className="p-4">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">Overall Completion</div>
        <div className="mt-1 flex items-baseline gap-2">
          <div className="font-heading text-3xl font-black tracking-tighter text-slate-900" data-testid="itp-overall">{progress.overall_pct}%</div>
        </div>
        <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-100">
          <div className="h-full bg-orange-500 transition-all" style={{ width: `${progress.overall_pct}%` }} />
        </div>
        <div className="mt-4 grid grid-cols-3 gap-3 text-center">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Signatures</div>
            <div className="mt-0.5 text-lg font-bold text-slate-900" data-testid="itp-signatures">{progress.signature_pct}%</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Evidence</div>
            <div className="mt-0.5 text-lg font-bold text-slate-900" data-testid="itp-evidence">{progress.evidence_pct}%</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Overdue</div>
            <div className={`mt-0.5 text-lg font-bold ${progress.overdue_count > 0 ? "text-rose-700" : "text-slate-900"}`} data-testid="itp-overdue">{progress.overdue_count}</div>
          </div>
        </div>
      </div>
      <div className="border-t border-slate-100 px-4 py-3">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-semibold text-slate-700">Team at a Glance</h4>
          <Link to="/users" className="text-[10px] font-semibold text-teal-700 hover:underline" data-testid="team-view-link">View Team →</Link>
        </div>
        <div className="mt-2 grid grid-cols-4 gap-2 text-center text-xs">
          {(team || []).map((t) => (
            <div key={t.role} data-testid={`team-${t.role}`}>
              <div className="grid size-8 place-items-center rounded-full bg-slate-100 mx-auto"><UsersIcon className="size-3.5 text-slate-600" /></div>
              <div className="mt-1 font-bold text-slate-900">{t.count}</div>
              <div className="text-[10px] text-slate-500">{t.label}</div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Bottom strip
// -----------------------------------------------------------------------------
function BottomStrip({ strip }) {
  if (!strip) return null;
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="dashboard-bottom-strip">
      <StripItem icon={Cloud} label="Next Weather Window" primary={strip.next_weather_window.range} secondary={strip.next_weather_window.note} />
      <StripItem icon={Flag} label="Upcoming Milestone" primary={strip.upcoming_milestone.title} secondary={strip.upcoming_milestone.date} />
      <StripItem icon={ClipboardList} label="Today's Inspections" primary={`${strip.todays_inspections.scheduled} Scheduled`} secondary={`${strip.todays_inspections.completed} Completed`} />
      <StripItem icon={Building2} label="Documents Updated" primary={String(strip.documents_updated_this_week)} secondary="This Week" />
    </div>
  );
}

function StripItem({ icon: Icon, label, primary, secondary }) {
  return (
    <Card className="flex items-center gap-3 border-slate-200 p-3">
      <div className="grid size-9 place-items-center rounded-md bg-slate-100 text-slate-600"><Icon className="size-4" /></div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
        <div className="truncate text-sm font-semibold text-slate-900">{primary}</div>
        <div className="truncate text-[10px] text-slate-500">{secondary}</div>
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Skeleton block
// -----------------------------------------------------------------------------
function SkeletonBlock({ height = 200 }) {
  return <div className="animate-pulse rounded-md bg-slate-100" style={{ height }} />;
}
